import { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import COVER_IMAGE from "../assets/login_image.jpg";

// ResetPassword component handles setting new password with reset token
const ResetPassword = () => {
  // State management
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const { token } = useParams();
  const navigate = useNavigate();

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);

    // Validate password match
    if (password !== confirmPassword) {
      setError("Passwords don't match");
      return;
    }

    setLoading(true);

    try {
      const response = await fetch('/api/reset-password', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token, newPassword: password }),
      });

      const data = await response.text();
      if (!response.ok) throw new Error(data || 'Failed to reset password.');

      // Redirect to login with success message
      navigate('/', {
        state: { message: 'Password reset successful. Please log in with your new password.' }
      });
    } catch (error) {
      setError(error.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="w-full h-screen flex items-start">
      {/* Left side - Cover image and text */}
      <div className="relative w-1/2 h-full flex flex-col">
        <div className="absolute top-[20%] left-[10%] flex flex-col">
          <h1 className="text-4xl text-white font-bold my-4">Set New Password</h1>
          <p className="text-xl text-white font-normal">Choose a new password for your account</p>
        </div>
        <img src={COVER_IMAGE} className="w-full h-full object-cover" alt="Cover" />
      </div>

      {/* Right side - Password reset form */}
      <div className="w-1/2 h-full bg-[#F5F5F5]">
        <div className="p-20 flex flex-col justify-between h-full">
          <h1 className="text-xl font-semibold">SwopShop</h1>

          <div className="flex flex-col max-w-[550px]">
            <form onSubmit={handleSubmit} className="space-y-6">
              {/* New password input */}
              <div>
                <label htmlFor="password" className="block text-sm font-medium text-gray-700">
                  New Password
                </label>
                <input
                  type="password"
                  id="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="w-full py-2 my-2 bg-transparent border-b border-black outline-none focus:border-blue-500"
                  required
                  disabled={loading}
                />
              </div>

              {/* Confirm password input */}
              <div>
                <label htmlFor="confirmPassword" className="block text-sm font-medium text-gray-700">
                  Confirm New Password
                </label>
                <input
                  type="password"
                  id="confirmPassword"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  className="w-full py-2 my-2 bg-transparent border-b border-black outline-none focus:border-blue-500"
                  required
                  disabled={loading}
                />
              </div>

              {/* Error display */}
              {error && (
                <div className="bg-red-50 border-l-4 border-red-500 p-4">
                  <div className="flex">
                    <div className="ml-3">
                      <p className="text-sm text-red-700">{error}</p>
                    </div>
                  </div>
                </div>
              )}

              {/* Submit button */}
              <button
                type="submit"
                disabled={loading}
                className={`w-full bg-[#060606] text-white rounded-md p-4
                  ${loading ? 'opacity-70 cursor-not-allowed' : 'hover:opacity-90'}`}
              >
                {loading ? 'Resetting...' : 'Reset Password'}
              </button>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ResetPassword;
